import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Device } from '../services/deviceService';
import deviceService from '../services/deviceService';
import { StreamList } from '../components/StreamList';
import VideoPostList from '../components/VideoPostList';
import '../App.css';

const DeviceDetail: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const { user, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const [device, setDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editingName, setEditingName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDevice();
  }, [deviceId]);

  const loadDevice = async () => {
    if (!deviceId) return;
    
    setLoading(true);
    setError('');
    try {
      const devices = await deviceService.getDevices();
      const foundDevice = devices.find(d => d.id === parseInt(deviceId));
      if (foundDevice) {
        setDevice(foundDevice);
        setEditingName(foundDevice.device_name || '');
      } else {
        setError('Device not found');
      }
    } catch (err: any) {
      setError('Failed to load device');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDeleteDevice = async () => {
    if (!device || !window.confirm('Are you sure you want to remove this device?')) {
      return;
    }

    setDeleting(true);
    setError('');
    try {
      await deviceService.deleteDevice(device.id);
      navigate('/');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to delete device';
      setError(message);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveName = async () => {
    if (!device || !editingName.trim()) return;

    setSaving(true);
    try {
      const updated = await deviceService.updateDevice(device.id, { device_name: editingName.trim() });
      setDevice(updated);
      setIsEditing(false);
    } catch (err: any) {
      setError('Failed to update device name');
    } finally {
      setSaving(false);
    }
  };
  const handleRefreshPreview = async () => {
    if (!device) return;

    setRefreshing(true);
    try {
      await deviceService.refreshDeviceLED(device.id);
    } catch (err: any) {
      setError('Failed to refresh device LED');
    } finally {
      setRefreshing(false);
    }
  };
  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            Stream Signal
          </Link>
          <button 
            className="hamburger-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
          <div className={`nav-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            {user ? (
              <>
                <span className="nav-user">Welcome, {user.username}</span>
                <Link to="/help" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Help
                </Link>
                <button onClick={toggleDarkMode} className="nav-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  {isDarkMode ? '☀️' : '🌙'}
                </button>
                <button onClick={handleLogout} className="nav-logout">
                  Logout
                </button>
              </>
            ) : null}
          </div>
        </div>
      </nav>

      <main className="main-content">
        <div className="devices-container">
          <div className="device-detail-header">
            <Link to="/" className="back-link">← Back to Devices</Link>
          </div>

          {loading ? (
            <div className="loading">Loading device...</div>
          ) : error && !device ? (
            <div className="error-message">{error}</div>
          ) : device ? (
            <>
              <div className="device-detail-card">
                <div className="device-detail-title">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    {isEditing ? (
                      <div className="device-name-edit">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleSaveName()}
                          autoFocus
                          disabled={saving}
                          className="device-name-input"
                        />
                        <button onClick={handleSaveName} disabled={saving || !editingName.trim()} className="btn-save-name">
                          {saving ? '...' : '✓'}
                        </button>
                        <button onClick={() => { setIsEditing(false); setEditingName(device.device_name || ''); }} disabled={saving} className="btn-cancel-name">
                          ✕
                        </button>
                      </div>
                    ) : (
                      <h2 onClick={() => setIsEditing(true)} style={{ cursor: 'pointer' }} title="Click to edit">
                        {device.device_name || 'Unnamed Device'} ✏️
                      </h2>
                    )}
                  </div>
                </div>
                <div className="device-detail-hash">
                  <strong>Device Hash:</strong>
                  <code>{device.device_hash}</code>
                </div>
                <div className="device-detail-type">
                  <strong>Device Type:</strong>
                  <span className="device-type-badge-detail">{device.device_type || 'RGB'}</span>
                </div>
                {error && <div className="error-message">{error}</div>}
                <div className="device-actions" style={{ marginTop: 20 }}>
                  <button
                    onClick={handleDeleteDevice}
                    disabled={deleting}
                    className="delete-btn"
                  >
                    {deleting ? 'Deleting...' : 'Delete Device'}
                  </button>
                </div>
              </div>

              <StreamList device={device} />

              <VideoPostList device={device} />
            </>
          ) : null}
        </div>
      </main>
    </div>
  );
};

export default DeviceDetail;
